import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import TriangleDownIcon from '@/public/icons/approval/triangle-down.svg';
import TriangleRightIcon from '@/public/icons/approval/triangle-right.svg';
import {
  INFLUENCER_PPL_PURPOSE_CLASS,
  type InfluencerPplSavedInfo,
} from '@/utils/approval/influencerPpl';
import CategoryGraph from './CategoryGraph';
import {
  INFLUENCER_PPL_DETAIL_BUTTON_ID,
  INFLUENCER_PPL_GRAPH_PREFIX,
  INFLUENCER_PPL_HISTORY_ROW_PREFIX,
  INFLUENCER_PPL_ROW_TOGGLE_CLASS,
} from './content';
import {
  InfluencerPplGraphBox,
  InfluencerPplPurposeCell,
  InfluencerPplRoot,
  InfluencerPplRowToggleButton,
} from './styles';

export { buildInfluencerPplContentHtml } from './content';
export { default as InfluencerPplDetailDialog } from './DetailDialog';

const DROP_TAGS = new Set(['style', 'script', 'link']);
const TABLE_PARTS = new Set([
  'table',
  'colgroup',
  'thead',
  'tbody',
  'tfoot',
  'tr',
]);
/** 레거시 작성 화면이 이미지 배열을 문자열로 이어 붙여 저장 본문에 쉼표가 남음 */
const isBlankOrComma = (node: ReactNode) =>
  typeof node === 'string' && /^[\s,]*$/.test(node);

const hasClass = (element: Element, name: string) =>
  element.classList.contains(name);

type SnapshotProps = {
  content: string;
  /** 그래프·「상세보기」를 그릴 데이터(레거시 window.influencer 모양) */
  info: InfluencerPplSavedInfo | null;
  /** 레거시: 작성 화면은 검색량 선만, 조회 화면은 노출건수 막대까지 */
  showExposure: boolean;
  onOpenDetail: () => void;
  /** 작성 화면 — 목적 칸을 입력칸으로 */
  purposeInput?: ReactNode;
};

/** 저장 content(레거시 문서 포함) 위에 상세보기 버튼·카테고리 펼치기·그래프를 다시 그림 */
export const InfluencerPplSnapshot = ({
  content,
  info,
  showExposure,
  onOpenDetail,
  purposeInput,
}: SnapshotProps) => {
  const { t } = useTranslation();
  const [closedRows, setClosedRows] = useState<Set<string>>(new Set());

  const toggleRow = useCallback((rowId: string) => {
    setClosedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      const key = props.key as string;
      if (DROP_TAGS.has(tagName) || hasClass(element, 'modal')) return null;

      if (element.id === INFLUENCER_PPL_DETAIL_BUTTON_ID) {
        return (
          <BoxButton
            key={key}
            type="button"
            variant="secondary"
            size="small"
            onClick={onOpenDetail}
          >
            {t('상세보기')}
          </BoxButton>
        );
      }

      if (hasClass(element, INFLUENCER_PPL_ROW_TOGGLE_CLASS)) {
        const rowId = (element.getAttribute('data-target') ?? '').replace(
          /^#/,
          '',
        );
        const open = !closedRows.has(rowId);
        return (
          <InfluencerPplRowToggleButton
            key={key}
            size="small"
            aria-expanded={open}
            aria-label={open ? t('닫기') : t('펼치기')}
            title={open ? t('닫기') : t('펼치기')}
            onClick={() => toggleRow(rowId)}
          >
            {open ? <TriangleDownIcon /> : <TriangleRightIcon />}
          </InfluencerPplRowToggleButton>
        );
      }

      if (
        tagName === 'tr' &&
        element.id.startsWith(INFLUENCER_PPL_HISTORY_ROW_PREFIX)
      ) {
        if (closedRows.has(element.id)) return null;
        delete props.className;
      }

      if (
        tagName === 'canvas' &&
        element.id.startsWith(INFLUENCER_PPL_GRAPH_PREFIX)
      ) {
        const idx = Number(
          element.id.slice(INFLUENCER_PPL_GRAPH_PREFIX.length),
        );
        const category = info?.categoryChart?.[idx]?.category;
        const graph = info?.categoryGraph?.find((g) => g.category === category);
        if (!graph) return null;
        return (
          <InfluencerPplGraphBox key={key}>
            <CategoryGraph graph={graph} showExposure={showExposure} />
          </InfluencerPplGraphBox>
        );
      }

      if (
        purposeInput !== undefined &&
        tagName === 'td' &&
        hasClass(element, INFLUENCER_PPL_PURPOSE_CLASS)
      ) {
        return (
          <InfluencerPplPurposeCell key={key} className="text-left">
            {purposeInput}
          </InfluencerPplPurposeCell>
        );
      }

      // 2023년 초 레거시 문서는 목적 입력칸을 글자로 바꾸지 않고 저장함
      if (tagName === 'textarea') {
        return (
          <span key={key} className="ppl-purpose-text">
            {element.textContent}
          </span>
        );
      }

      if (tagName === 'table') delete props.style;
      if (TABLE_PARTS.has(tagName) || tagName === 'div') {
        const kept = children.filter((c) => !isBlankOrComma(c));
        children.splice(0, children.length, ...kept);
      }
      return undefined;
    },
    [closedRows, info, onOpenDetail, purposeInput, showExposure, t, toggleRow],
  );

  const rendered = useHtmlToReact(content, { transform });
  return <InfluencerPplRoot>{rendered}</InfluencerPplRoot>;
};
